import { useTranslation } from 'react-i18next';
import PageHeader from '@/components/page-header';
import SettingsLayout from '@/components/settings-layout';
import { localTenant, updateMe } from '@/services/session-service';
import { Tenant } from '@turizi/shared';
import { useState } from 'react';
import { setWebTitle } from '@/lib/format-utils';
import { TextInput, Textarea, Button, TagsInput } from '@mantine/core';
import { useUpdateTenant } from '@/hooks/api-hooks';
import CountrySelector from '@/components/country-selector';
import { Link } from 'react-router-dom';
import { IconExternalLink } from '@tabler/icons-react';
import ImageUploadPreview from '@/components/image-upload-preview';
import ServiceCategoriesRanks from '@/components/service-categories-ranks';
import SettingsCard from '@/components/settings-card';
import { notify } from '@/lib/notify';

// TODO this page needs to be updated, the data changed locations
// inside the settings.store object
export default function SettingsOnlineStorePage(): JSX.Element {
    const { t } = useTranslation();
    const tenant = localTenant();

    const updateTenantMutation = useUpdateTenant();
    const [draft, setDraft] = useState<Tenant | null>(tenant);

    setWebTitle(t('online_store'));

    const handleSaveUrl = () => {
        if (!tenant) return;
        updateTenantMutation.mutate({
            id: tenant.id,
            settings: {
                store: { url: draft?.settings?.store?.url }
            }
        }, {
            onSuccess: async () => {
                await updateMe(true);
                notify.success(t('settings_saved_success', { defaultValue: 'Settings saved successfully' }), t('success'));
            }
        });
    };

    const handleSaveContact = () => {
        if (!tenant) return;
        updateTenantMutation.mutate({
            id: tenant.id,
            country_code: draft?.country_code,
            settings: {
                address: draft?.settings?.address,
                city: draft?.settings?.city,
                phone: draft?.settings?.phone,
                email: draft?.settings?.email,
                instagram: draft?.settings?.instagram
            }
        }, {
            onSuccess: async () => {
                await updateMe(true);
                notify.success(t('settings_saved_success', { defaultValue: 'Settings saved successfully' }), t('success'));
            }
        });
    };

    const handleSaveStoreDetails = () => {
        if (!tenant) return;
        updateTenantMutation.mutate({
            id: tenant.id,
            settings: {
                logo_url: draft?.settings?.logo_url,
                icon_url: draft?.settings?.icon_url,
                store: {
                    display_name: draft?.settings?.store?.display_name,
                    hero_title: draft?.settings?.store?.hero_title,
                    hero_subtitle: draft?.settings?.store?.hero_subtitle,
                    hero_image_url: draft?.settings?.store?.hero_image_url,
                    background_image_url: draft?.settings?.store?.background_image_url
                }
            }
        }, {
            onSuccess: async () => {
                await updateMe(true);
                notify.success(t('settings_saved_success', { defaultValue: 'Settings saved successfully' }), t('success'));
            }
        });
    };

    const handleSaveDesign = () => {
        if (!tenant) return;
        updateTenantMutation.mutate({
            id: tenant.id,
            settings: {
                store: {
                    primary_color: draft?.settings?.store?.primary_color,
                    secondary_color: draft?.settings?.store?.secondary_color,
                    background_color: draft?.settings?.store?.background_color,
                    font_family: draft?.settings?.store?.font_family
                }
            }
        }, {
            onSuccess: async () => {
                await updateMe(true);
                notify.success(t('settings_saved_success', { defaultValue: 'Settings saved successfully' }), t('success'));
            }
        });
    };

    const updateSettingValue = (field: keyof Tenant['settings'], value: any) => {
        setDraft(prev => prev ? ({ ...prev, settings: { ...prev.settings, [field]: value } }) : null);
    };

    const updateStoreSettingValue = (field: string, value: string) => {
        setDraft(prev => {
            if (!prev) return null;
            const currentStore = prev.settings.store || {};
            return {
                ...prev,
                settings: {
                    ...prev.settings,
                    store: {
                        ...currentStore,
                        [field]: value
                    }
                }
            };
        });
    };

    return (
        <div className="panel-content max-w-5xl">
            <SettingsLayout>
                <div className='flex flex-col gap-6 w-full'>
                    <SettingsCard
                        title={t('url')}
                        description={t('store_url_description', { defaultValue: 'The public URL for your online store.' })}
                        onSave={handleSaveUrl}
                        saving={updateTenantMutation.isPending}
                    >
                        <TextInput
                            size='xl'
                            className='font-bold'
                            value={draft?.settings?.store?.url || 'url-not-set!'}
                            onChange={(e) => updateStoreSettingValue('url', e.target.value)}
                        />
                    </SettingsCard>

                    <div className="w-full">
                        <ServiceCategoriesRanks />
                    </div>

                    <SettingsCard
                        title={t('contact')}
                        description={t('store_contact_description', { defaultValue: 'Contact information displayed to your customers.' })}
                        onSave={handleSaveContact}
                        saving={updateTenantMutation.isPending}
                    >
                        <CountrySelector
                            size='lg'
                            className='flex-1'
                            value={draft?.country_code || ''}
                            onChange={(value) => setDraft({ ...draft, country_code: value })}
                        />
                        <TextInput
                            label={t('address')}
                            value={draft?.settings?.address || ''}
                            onChange={(e) => updateSettingValue('address', e.target.value)}
                        />
                        <TextInput
                            label={t('city')}
                            value={draft?.settings?.city || ''}
                            onChange={(e) => updateSettingValue('city', e.target.value)}
                        />
                        <TextInput
                            label={t('phone')}
                            value={draft?.settings?.phone || ''}
                            onChange={(e) => updateSettingValue('phone', e.target.value)}
                        />
                        <TextInput
                            label={t('email')}
                            type="email"
                            value={draft?.settings?.email || ''}
                            onChange={(e) => updateSettingValue('email', e.target.value)}
                        />
                        <TextInput
                            label={t('instagram')}
                            value={draft?.settings?.instagram || ''}
                            onChange={(e) => updateSettingValue('instagram', e.target.value)}
                        />
                    </SettingsCard>

                    <SettingsCard
                        title={t('store')}
                        description={t('store_details_description', { defaultValue: 'General presentation details of your store frontend.' })}
                        onSave={handleSaveStoreDetails}
                        saving={updateTenantMutation.isPending}
                    >
                        <TextInput
                            label={t('display_name')}
                            value={draft?.settings?.store?.display_name || ''}
                            onChange={(e) => updateStoreSettingValue('display_name', e.target.value)}
                        />
                        <TextInput
                            label={t('hero_title')}
                            value={draft?.settings?.store?.hero_title || ''}
                            onChange={(e) => updateStoreSettingValue('hero_title', e.target.value)}
                        />
                        <TextInput
                            label={t('hero_subtitle')}
                            value={draft?.settings?.store?.hero_subtitle || ''}
                            onChange={(e) => updateStoreSettingValue('hero_subtitle', e.target.value)}
                        />
                        <ImageUploadPreview
                            label={t('logo')}
                            value={draft?.settings?.logo_url}
                            uploadKey="logo_url"
                            onChange={(url) => updateSettingValue('logo_url', url)}
                        />
                        <ImageUploadPreview
                            label={t('icon')}
                            value={draft?.settings?.icon_url}
                            uploadKey="icon_url"
                            onChange={(url) => updateSettingValue('icon_url', url)}
                        />
                        <ImageUploadPreview
                            label={t('hero_image_url')}
                            value={draft?.settings?.store?.hero_image_url}
                            uploadKey="store.hero_image_url"
                            onChange={(url) => updateStoreSettingValue('hero_image_url', url)}
                        />
                        <ImageUploadPreview
                            label={t('background_image_url')}
                            value={draft?.settings?.store?.background_image_url}
                            uploadKey="store.background_image_url"
                            onChange={(url) => updateStoreSettingValue('background_image_url', url)}
                        />
                    </SettingsCard>

                    <SettingsCard
                        title={t('design')}
                        description={t('store_design_description', { defaultValue: 'Customize colors and fonts of your online store.' })}
                        onSave={handleSaveDesign}
                        saving={updateTenantMutation.isPending}
                    >
                        <TextInput
                            label={t('primary_color')}
                            type="color"
                            value={draft?.settings?.store?.primary_color || '#000000'}
                            onChange={(e) => updateStoreSettingValue('primary_color', e.target.value)}
                        />
                        <TextInput
                            label={t('secondary_color')}
                            type="color"
                            value={draft?.settings?.store?.secondary_color || '#ffffff'}
                            onChange={(e) => updateStoreSettingValue('secondary_color', e.target.value)}
                        />
                        <TextInput
                            label={t('background_color')}
                            type="color"
                            value={draft?.settings?.store?.background_color || '#f5f7fa'}
                            onChange={(e) => updateStoreSettingValue('background_color', e.target.value)}
                        />
                        <TextInput
                            label={t('font_family')}
                            placeholder="Inter"
                            description="Google Font Name"
                            value={draft?.settings?.store?.font_family || ''}
                            onChange={(e) => updateStoreSettingValue('font_family', e.target.value)}
                        />
                    </SettingsCard>
                </div>
            </SettingsLayout>
        </div>
    );
}
